import { t } from "@lingui/macro";
import graphql from "babel-plugin-relay/macro";
import { useCallback, useState } from "react";
import { useFragment, useMutation } from "react-relay";

import {
  LoyaltyReward,
  LoyaltyRewardsModal,
} from "@/features/Loyalty/components/LoyaltyRewardsModal";
import { useTrackEvent } from "@/utils/Analytics/Mixpanel/hooks/useTrackEvent";
import { useFlags } from "@/utils/launchDarkly/useFlags";
import {
  CloseIconSmall,
  LoyaltyIcon,
} from "@whoosh/design-system/src/components/icons_v2/general";
import { useToast } from "@whoosh/design-system/src/components/overlay/Toast/useToast";
import {
  Box,
  HStack,
  Pressable,
  Text,
  VStack,
} from "@whoosh/design-system/src/components/primitive";

import { PatronCheckoutLoyaltyEnrollment } from "./PatronCheckoutLoyaltyEnrollment";
import type { PatronCheckoutLoyaltyRewardsApplyMutation } from "./__generated__/PatronCheckoutLoyaltyRewardsApplyMutation.graphql";
import type { PatronCheckoutLoyaltyRewardsEnrollmentOffer_order$key } from "./__generated__/PatronCheckoutLoyaltyRewardsEnrollmentOffer_order.graphql";
import type { PatronCheckoutLoyaltyRewardsRemoveMutation } from "./__generated__/PatronCheckoutLoyaltyRewardsRemoveMutation.graphql";
import type {
  PatronCheckoutLoyaltyRewards_order$data,
  PatronCheckoutLoyaltyRewards_order$key,
} from "./__generated__/PatronCheckoutLoyaltyRewards_order.graphql";

type LoyaltyData = NonNullable<
  PatronCheckoutLoyaltyRewards_order$data["availableLoyaltyRewards"]
>;
type RewardTierEntry = LoyaltyData["rewardTiers"][number];
export type PatronCheckoutLoyaltyReward =
  PatronCheckoutLoyaltyRewards_order$data["squareLoyaltyRewards"][number];
export type PatronCheckoutLoyaltyAnalyticsProperties =
  | { bookingId: string }
  | { eventSignUpId: string };

export const isAppliedPatronLoyaltyReward = (
  status: PatronCheckoutLoyaltyReward["status"]
) => status === "ISSUED" || status === "REDEEMED";

const isEditablePatronLoyaltyOrder = (
  status: PatronCheckoutLoyaltyRewards_order$data["status"]
) => status === "DRAFT" || status === "OPEN" || status === "PARTIALLY_PAID";

type PatronCheckoutLoyaltyRewardsProps = {
  analyticsProperties: PatronCheckoutLoyaltyAnalyticsProperties;
  display?: "all" | "rewards" | "enrollment";
  editable?: boolean;
  enrollmentOfferRef?: PatronCheckoutLoyaltyRewardsEnrollmentOffer_order$key | null;
  loyaltyRewards?: readonly PatronCheckoutLoyaltyReward[];
  orderRef: PatronCheckoutLoyaltyRewards_order$key | null;
  section?: "all" | "applied" | "action";
};

export const PatronCheckoutLoyaltyRewards = ({
  analyticsProperties,
  display = "all",
  editable = true,
  enrollmentOfferRef,
  loyaltyRewards,
  orderRef,
  section = "all",
}: PatronCheckoutLoyaltyRewardsProps) => {
  const { patronLoyalty } = useFlags();
  const order = useFragment(
    PatronCheckoutLoyaltyRewardsOrderFragment,
    orderRef
  );
  const enrollmentOfferData = useFragment(
    PatronCheckoutLoyaltyRewardsEnrollmentOfferOrderFragment,
    enrollmentOfferRef ?? null
  );
  const enrollmentOffer = enrollmentOfferData?.loyaltyEnrollmentOffer;
  const loyalty = order?.availableLoyaltyRewards;

  if (!patronLoyalty || !order) {
    return null;
  }

  const appliedRewards = (loyaltyRewards ?? order.squareLoyaltyRewards).filter(
    (reward) => isAppliedPatronLoyaltyReward(reward.status)
  );
  if (
    display !== "enrollment" &&
    (loyalty || (appliedRewards.length > 0 && section !== "action"))
  ) {
    return (
      <PatronCheckoutLoyaltyRewardsContent
        analyticsProperties={analyticsProperties}
        editable={editable && isEditablePatronLoyaltyOrder(order.status)}
        loyalty={
          loyalty ?? {
            balance: 0,
            terminologyOne: enrollmentOffer?.terminologyOne ?? t`point`,
            terminologyOther: enrollmentOffer?.terminologyOther ?? t`points`,
            rewardTiers: [],
          }
        }
        isEnrolled={loyalty != null}
        loyaltyRewards={loyaltyRewards ?? order.squareLoyaltyRewards}
        order={order}
        section={section}
      />
    );
  }

  if (display !== "rewards" && enrollmentOffer) {
    return (
      <PatronCheckoutLoyaltyEnrollment
        points={enrollmentOffer.points}
        terminologyOne={enrollmentOffer.terminologyOne}
        terminologyOther={enrollmentOffer.terminologyOther}
      />
    );
  }

  return null;
};

type ContentProps = {
  analyticsProperties: PatronCheckoutLoyaltyAnalyticsProperties;
  editable: boolean;
  isEnrolled: boolean;
  loyalty: LoyaltyData;
  loyaltyRewards: readonly PatronCheckoutLoyaltyReward[];
  order: PatronCheckoutLoyaltyRewards_order$data;
  section: "all" | "applied" | "action";
};

const PatronCheckoutLoyaltyRewardsContent = ({
  analyticsProperties,
  editable,
  isEnrolled,
  loyalty,
  loyaltyRewards,
  order,
  section,
}: ContentProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [commitApplyReward, isApplying] =
    useMutation<PatronCheckoutLoyaltyRewardsApplyMutation>(applyRewardMutation);
  const [commitRemoveReward, isRemoving] =
    useMutation<PatronCheckoutLoyaltyRewardsRemoveMutation>(
      removeRewardMutation
    );
  const trackEvent = useTrackEvent();
  const { showToast } = useToast();
  const shouldShowAppliedRewards = section !== "action";
  const shouldShowAction = isEnrolled && editable && section !== "applied";

  const availableCount = loyalty.rewardTiers.filter(
    (tier) => tier.status === "AVAILABLE"
  ).length;
  const rewards: LoyaltyReward[] = loyalty.rewardTiers.map((tier) => ({
    id: tier.rewardTier.id,
    name: tier.rewardTier.name,
    points: tier.rewardTier.points,
    status: tier.status,
  }));

  const showMutationError = useCallback(
    (action: "apply" | "remove") => {
      showToast({
        title:
          action === "apply"
            ? t`Couldn't apply reward`
            : t`Couldn't remove reward`,
        description: t`Please try again.`,
        semanticStyle: "error",
      });
    },
    [showToast]
  );

  const applyReward = useCallback(
    (tier: RewardTierEntry) => {
      commitApplyReward({
        variables: {
          input: {
            orderId: order.id,
            rewardTierId: tier.rewardTier.id,
          },
        },
        onCompleted: (response) => {
          const result = response.applyLoyaltyReward;
          if (!result?.success || result.errors.length > 0) {
            showMutationError("apply");
            return;
          }

          setIsModalOpen(false);
        },
        onError: () => showMutationError("apply"),
      });
    },
    [commitApplyReward, order.id, showMutationError]
  );

  const handleSelectReward = useCallback(
    (reward: LoyaltyReward) => {
      const tier = loyalty.rewardTiers.find(
        (candidate) => candidate.rewardTier.id === reward.id
      );
      if (!tier || tier.status !== "AVAILABLE") {
        return;
      }

      trackEvent("BookingDetail.AvailableRewardClicked", analyticsProperties);
      applyReward(tier);
    },
    [analyticsProperties, applyReward, loyalty.rewardTiers, trackEvent]
  );

  const removeReward = useCallback(
    (squareLoyaltyRewardId: string) => {
      commitRemoveReward({
        variables: { input: { squareLoyaltyRewardId } },
        onCompleted: (response) => {
          const result = response.removeLoyaltyReward;
          if (!result?.success || result.errors.length > 0) {
            showMutationError("remove");
          }
        },
        onError: () => showMutationError("remove"),
      });
    },
    [commitRemoveReward, showMutationError]
  );

  return (
    <VStack gap={4} testID="booking-loyalty-rewards">
      {shouldShowAppliedRewards
        ? loyaltyRewards
            .filter((reward) => isAppliedPatronLoyaltyReward(reward.status))
            .map((reward) => (
              <AppliedRewardRow
                key={reward.id}
                name={reward.rewardTier.name}
                points={reward.points}
                pointTerminologyOne={loyalty.terminologyOne}
                pointTerminologyOther={loyalty.terminologyOther}
                isRemoving={isRemoving}
                onRemove={editable ? () => removeReward(reward.id) : undefined}
              />
            ))
        : null}
      {shouldShowAction ? (
        <>
          <Pressable
            accessibilityLabel={
              availableCount > 0 ? t`Redeem Reward` : t`No Available Rewards`
            }
            accessibilityRole="button"
            testID="redeem-reward-button"
            onPress={() => {
              trackEvent(
                "BookingDetail.RedeemRewardClicked",
                analyticsProperties
              );
              setIsModalOpen(true);
            }}
          >
            <HStack alignItems="center" gap={3}>
              <LoyaltyIconChip isAvailable={availableCount > 0} />
              <HStack alignItems="center" gap={2}>
                <Text
                  variant="mainBaseSemiBold"
                  color={
                    availableCount > 0
                      ? "text-accent-purple-primary"
                      : "text-secondary"
                  }
                >
                  {availableCount > 0
                    ? t`Redeem Reward`
                    : t`No Available Rewards`}
                </Text>
                {availableCount > 0 ? (
                  <Box
                    width={18}
                    height={18}
                    borderRadius="6"
                    backgroundColor="surface-accent-purple-subtler"
                    alignItems="center"
                    justifyContent="center"
                  >
                    <Text variant="mainTiny" color="text-accent-purple-primary">
                      {availableCount}
                    </Text>
                  </Box>
                ) : null}
              </HStack>
            </HStack>
          </Pressable>
          <LoyaltyRewardsModal
            isOpen={isModalOpen}
            onClose={() => setIsModalOpen(false)}
            currentBalance={loyalty.balance}
            rewards={rewards}
            pointTerminologyOne={loyalty.terminologyOne}
            pointTerminologyOther={loyalty.terminologyOther}
            pointExpirations={
              order.orderOwner?.squareLoyaltyAccount?.expiringPointDeadlines ??
              []
            }
            timeZone={order.orderOwner?.club?.timeZone ?? "Etc/UTC"}
            isLoading={isApplying}
            onSelectReward={handleSelectReward}
          />
        </>
      ) : null}
    </VStack>
  );
};

const LoyaltyIconChip = ({ isAvailable }: { isAvailable: boolean }) => (
  <Box
    width={32}
    height={20}
    borderRadius="2"
    backgroundColor={
      isAvailable ? "surface-accent-purple-subtler" : "surface-secondary"
    }
    alignItems="center"
    justifyContent="center"
  >
    <LoyaltyIcon
      color={isAvailable ? "text-accent-purple-primary" : "icon-secondary"}
      width={16}
      height={16}
    />
  </Box>
);

type AppliedRewardRowProps = {
  name: string;
  points: number;
  pointTerminologyOne: string;
  pointTerminologyOther: string;
  isRemoving: boolean;
  onRemove?: () => void;
};

const AppliedRewardRow = ({
  name,
  points,
  pointTerminologyOne,
  pointTerminologyOther,
  isRemoving,
  onRemove,
}: AppliedRewardRowProps) => (
  <HStack
    alignItems="center"
    gap={3}
    backgroundColor="surface-primary"
    borderRadius="2"
    paddingHorizontal={3}
    paddingVertical={1}
    borderWidth={4}
    borderColor="surface-secondary"
  >
    <LoyaltyIconChip isAvailable={false} />
    <VStack flex={1}>
      <Text variant="mainSmallSemiBold" color="text-primary">
        {name}
      </Text>
      <Text variant="mainTiny" color="text-secondary">
        -{points} {points === 1 ? pointTerminologyOne : pointTerminologyOther}
      </Text>
    </VStack>
    {onRemove ? (
      <Pressable
        accessibilityLabel={t`Remove ${name} reward`}
        accessibilityRole="button"
        accessibilityState={{ disabled: isRemoving }}
        testID={`remove-loyalty-reward-${name}`}
        backgroundColor="surface-status-error-subtlest"
        borderRadius="2"
        padding={2}
        disabled={isRemoving}
        opacity={isRemoving ? 0.6 : 1}
        onPress={onRemove}
      >
        <CloseIconSmall color="icon-status-error" width={16} height={16} />
      </Pressable>
    ) : null}
  </HStack>
);

export const PatronCheckoutLoyaltyRewardsOrderFragment = graphql`
  fragment PatronCheckoutLoyaltyRewards_order on Order {
    id
    status
    total
    totalTaxes
    totalDiscount
    bookings {
      id
      totalDueAfterCredits
    }
    expectedLoyaltyPoints
    orderOwner {
      squareLoyaltyAccount {
        expiringPointDeadlines {
          id
          points
          expiresAt
        }
      }
      club {
        timeZone
      }
    }
    availableLoyaltyRewards {
      balance
      terminologyOne
      terminologyOther
      rewardTiers {
        status
        rewardTier {
          id
          name
          points
        }
      }
    }
    squareLoyaltyRewards {
      id
      points
      status
      loyaltyCustomerId
      loyaltyAccount {
        id
      }
      rewardTier {
        id
        name
      }
    }
  }
`;

export const PatronCheckoutLoyaltyRewardsEnrollmentOfferOrderFragment = graphql`
  fragment PatronCheckoutLoyaltyRewardsEnrollmentOffer_order on Order {
    loyaltyEnrollmentOffer {
      points
      terminologyOne
      terminologyOther
    }
  }
`;

const applyRewardMutation = graphql`
  mutation PatronCheckoutLoyaltyRewardsApplyMutation(
    $input: ApplyLoyaltyRewardInput!
  ) {
    applyLoyaltyReward(input: $input) {
      success
      errors {
        code
      }
      order {
        ...PatronCheckoutLoyaltyRewards_order @relay(mask: false)
        ...PatronCheckoutLoyaltyRewardsBookingProjection_order
      }
    }
  }
`;

const removeRewardMutation = graphql`
  mutation PatronCheckoutLoyaltyRewardsRemoveMutation(
    $input: RemoveLoyaltyRewardInput!
  ) {
    removeLoyaltyReward(input: $input) {
      success
      errors {
        code
      }
      order {
        ...PatronCheckoutLoyaltyRewards_order @relay(mask: false)
        ...PatronCheckoutLoyaltyRewardsBookingProjection_order
      }
    }
  }
`;

export const PatronCheckoutLoyaltyRewardsBookingProjectionFragment = graphql`
  fragment PatronCheckoutLoyaltyRewardsBookingProjection_order on Order {
    bookings {
      id
      totalDueAfterCredits
      projectedCreditApplications {
        amount
        paymentSource
        squareGiftCard {
          id
          ganLastFour
        }
        raincheck {
          id
        }
      }
    }
  }
`;
